import {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useState
} from 'react'
import { toast } from 'react-toastify'

import { accessoriesService } from '@/services/Parametrization/accessoriesService'
import { breakdownService } from '@/services/Parametrization/breakdownService'
import { categoryService } from '@/services/Parametrization/categoryService'
import { companyService } from '@/services/Parametrization/companyService'
import { evaluationTypeService } from '@/services/Parametrization/evaluationTypeServices'
import { inspectionPhotosService } from '@/services/Parametrization/inspectionPhotosService'
import { markService } from '@/services/Parametrization/markService'
import { photoFlowService } from '@/services/Parametrization/photoFlowService'
import { photoSectionsService } from '@/services/Parametrization/photoSectionsService'
import { productService } from '@/services/Parametrization/productService'
import { providerService } from '@/services/Parametrization/providerService'
import { serialItemService } from '@/services/Parametrization/serialItemService'
import { servicesParametrizationService } from '@/services/Parametrization/servicesParametrizationService'
import { slaService } from '@/services/Parametrization/slaService'
import { utilityService } from '@/services/Parametrization/utilityService'
import { photoFlowServiceV2 } from '@/services/Parametrization/v2/photoFlowService'
import { addressService } from '@/services/utils/addressService'

import {
  PhotoFlowByIdResponse,
  PhotoFlowItemsByIdResponse
} from '@/types/photoFlow.types'
import { CONSTANTS, SUCCESS_STATUS } from '@/utils/CONSTANTS'

interface IParametrization {
  children: React.ReactNode
}

type ParametrizationContextData = {
  CATEGORY_LIST: any
  PROVIDER_LIST: any
  PROVIDER_YARD_LIST: any
  PRODUCT_LIST: any
  SERVICE_LIST: any
  SERIAL_ITEM_LIST: any
  ACCESSORIES_LIST: any
  MARK_LIST: any
  SLA_LIST: any
  BRAND_LIST: any[]
  BRAND_OPTIONS: any[]
  MEASUREMENT_LIST: any[]
  MEASUREMENT_OPTIONS: any[]
  BREAKDOWN_LIST: any
  CATEGORY_OPTIONS: any[]
  INTERPRISE_LIST: any
  INTERPRISE_OPTIONS: any[]
  EMPLOYEE_LIST: any[]
  HISTORIC_LIST: any[]
  STATE_LIST: any[]
  STATE_OPTIONS: any[]
  categoriaVistoria: any
  EVALUATION_TYPE_LIST: any
  INSPECTION_PHOTOS_LIST: any
  PHOTO_SECTIONS_LIST: any
  PHOTO_FLOW_LIST: any
  GET_CATEGORY: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_BREAKDOWNS: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_SLA: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_PRODUCTS: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_SERVICES: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_PROVIDER: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_PROVIDER_YARD_LIST: (params?: any) => Promise<any>
  GET_SERIAL_ITEMS: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_ACCESSORIES: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_INTERPRISE: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_MARKS: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_SELECTS: (params?: any) => Promise<void>
  GET_EMPLOYEES: (params?: any, isFilterApplied?: boolean) => Promise<void>
  obterCategoriaVistoria: (
    params?: any,
    isFilterApplied?: boolean
  ) => Promise<void>
  GET_HISTORIC: (params?: any) => void
  GET_EVALUATION_TYPE: (
    params?: any,
    isFilterApplied?: boolean
  ) => Promise<void>
  GET_INSPECTION_PHOTOS: (
    params?: any,
    isFilterApplied?: boolean
  ) => Promise<void>
  GET_PHOTO_SECTIONS: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_PHOTO_FLOWS: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_PHOTO_FLOW: (id: string) => Promise<PhotoFlowByIdResponse>
  SET_EMPLOYEE_LIST: Dispatch<SetStateAction<never[]>>
  SET_HISTORIC_LIST: Dispatch<SetStateAction<never[]>>
  SET_SLA_LIST: Dispatch<SetStateAction<never[]>>
  SET_BREAKDOWN_LIST: Dispatch<SetStateAction<never[]>>
  setCategoriaVistoria: Dispatch<SetStateAction<never[]>>
  FETCH_EVALUATION_TYPE: (params?: any) => Promise<void>
  FETCH_CATEGORY_GROUP: (params?: any) => Promise<void>
  FETCH_PHOTO_FLOW_ITENS: (
    id: string
  ) => Promise<PhotoFlowItemsByIdResponse | null>
  FETCH_PHOTO_SECTIONS: (params?: any) => Promise<any>
  FETCH_INSPECTION_PHOTOS: (params?: any) => Promise<void>
  ADD_PHOTO_FLOW: (payload: any) => Promise<void>
  UPDATE_PHOTO_FLOW: (id: string, payload: any) => Promise<void>
  UPDATE_PHOTOS_IN_SECTION: (id: string, payload: any) => Promise<void>
  ACTIVE_PHOTO_FLOW: (id: string) => Promise<void>
  INACTIVE_PHOTO_FLOW: (id: string) => Promise<void>
  VERIFY_PARAMETRIZATION_EXISTS: (params: any) => Promise<void>
  VERIFY_THIRD_PARTY_PARAMETRIZATION_EXISTS: () => Promise<boolean>
  GET_DETAILS_PHOTO_FLOW: (id: string) => Promise<void>
  CLONE_PHOTO_FLOW: (id: string, payload: any) => Promise<void>
}

export const ParametrizationContext = createContext(
  {} as ParametrizationContextData
)

export function ParametrizationProvider({ children }: IParametrization) {
  const [CATEGORY_LIST, SET_CATEGORY_LIST] = useState<any>({})
  const [CATEGORY_OPTIONS, SET_CATEGORY_OPTIONS] = useState([])
  const [BREAKDOWN_LIST, SET_BREAKDOWN_LIST] = useState({})
  const [SLA_LIST, SET_SLA_LIST] = useState({})
  const [PRODUCT_LIST, SET_PRODUCT_LIST] = useState({})
  const [SERVICE_LIST, SET_SERVICE_LIST] = useState({})
  const [PROVIDER_LIST, SET_PROVIDER_LIST] = useState({})
  const [PROVIDER_YARD_LIST, SET_PROVIDER_YARD_LIST] = useState({})
  const [SERIAL_ITEM_LIST, SET_SERIAL_ITEM_LIST] = useState({})
  const [ACCESSORIES_LIST, SET_ACCESSORIES_LIST] = useState([])
  const [INTERPRISE_LIST, SET_INTERPRISE_LIST] = useState<any>({})
  const [INTERPRISE_OPTIONS, SET_INTERPRISE_OPTIONS] = useState([])
  const [MARK_LIST, SET_MARK_LIST] = useState<any>({})
  const [BRAND_LIST, SET_BRAND_LIST] = useState([])
  const [BRAND_OPTIONS, SET_BRAND_OPTIONS] = useState([])
  const [MEASUREMENT_LIST, SET_MEASUREMENT_LIST] = useState([])
  const [MEASUREMENT_OPTIONS, SET_MEASUREMENT_OPTIONS] = useState([])
  const [EMPLOYEE_LIST, SET_EMPLOYEE_LIST] = useState([])
  const [HISTORIC_LIST, SET_HISTORIC_LIST] = useState([])
  const [STATE_LIST, SET_STATE_LIST] = useState([])
  const [STATE_OPTIONS, SET_STATE_OPTIONS] = useState([])
  const [categoriaVistoria, setCategoriaVistoria] = useState([])
  const [EVALUATION_TYPE_LIST, SET_EVALUATION_TYPE_LIST] = useState([])
  const [INSPECTION_PHOTOS_LIST, SET_INSPECTION_PHOTOS_LIST] = useState([])
  const [PHOTO_SECTIONS_LIST, SET_PHOTO_SECTIONS_LIST] = useState([])
  const [PHOTO_FLOW_LIST, SET_PHOTO_FLOW_LIST] = useState([])

  useEffect(() => {
    SET_DATA_SELECTS()
  }, [BRAND_LIST, MEASUREMENT_LIST, CATEGORY_LIST, INTERPRISE_LIST, STATE_LIST])

  async function GET_DETAILS_PHOTO_FLOW(id: string) {
    try {
      const res = await photoFlowService.detailsPhotoFlow(id)
      return res.data
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function CLONE_PHOTO_FLOW(id: string, payload: any) {
    try {
      const res = await photoFlowService.clonePhotoFlow(id, payload)
      return res.data
    } catch (e: any) {
      toast.error(e.message)
      throw e
    }
  }

  async function GET_CATEGORY(params?: any, isFilterApplied?: boolean) {
    try {
      const res: any = await categoryService.get(params)
      const categories = res.data.data

      if (!categories?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (categories?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_CATEGORY_LIST(categories)
      return categories
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function obterCategoriaVistoria(
    params?: any,
    isFilterApplied?: boolean
  ) {
    try {
      const res: any = await categoryService.categoriaVistoria(params)
      const cVitoria = res.data.data

      if (!cVitoria?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (cVitoria?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      setCategoriaVistoria(cVitoria)
      return cVitoria
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_INTERPRISE(params?: any, isFilterApplied?: boolean) {
    try {
      const res: any = await companyService.get(params)
      const interprises = res.data.data

      if (!interprises?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (interprises?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_INTERPRISE_LIST(interprises)
      return interprises
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_PRODUCTS(params?: any, isFilterApplied?: boolean) {
    try {
      const res: any = await productService.get(params)
      const products = res.data.data

      if (!products?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (products?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_PRODUCT_LIST(products)
      return products
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_PROVIDER(params?: any, isFilterApplied?: boolean) {
    try {
      const res: any = await providerService.get(params)
      const providers = res.data.data

      if (!providers?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (providers?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_PROVIDER_LIST(providers)
      return providers
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_SLA(params?: any, isFilterApplied?: boolean) {
    try {
      const res: any = await slaService.get(params)
      const sla = res.data.data

      if (!sla?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (sla?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_SLA_LIST(sla)
      return sla
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_SERVICES(params: any, isFilterApplied?: boolean) {
    try {
      const res: any = await servicesParametrizationService.get(params)
      const services = res.data.data

      if (!services?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (services?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_SERVICE_LIST(services)
      return services
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_BREAKDOWNS(params?: any, isFilterApplied?: boolean) {
    try {
      const res: any = await breakdownService.get(params)
      const breakdowns = res.data.data

      if (!breakdowns?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (breakdowns?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_BREAKDOWN_LIST(breakdowns)
      return breakdowns
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_SERIAL_ITEMS(params?: any, isFilterApplied?: boolean) {
    try {
      const res: any = await serialItemService.get(params)
      const serialItems = res.data.data

      if (!serialItems?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (serialItems?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_SERIAL_ITEM_LIST(serialItems)
      return serialItems
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_ACCESSORIES(params?: any, isFilterApplied?: boolean) {
    try {
      const res: any = await accessoriesService.get(params)
      const accessories = res.data.data

      if (!accessories?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (accessories?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_ACCESSORIES_LIST(accessories)
      return accessories
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_EMPLOYEES(params?: any, isFilterApplied?: boolean) {
    try {
      const res: any = await companyService.employees(params)
      const employees = res.data.data

      if (!employees?.length) toast.warning(CONSTANTS.filter.empty)
      if (employees?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_EMPLOYEE_LIST(employees)
      return employees
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_HISTORIC(id: string) {
    try {
      const res: any = await companyService.historic(id)
      const historic = res.data.data

      if (!historic?.length) return toast.warning(CONSTANTS.filter.empty)

      SET_HISTORIC_LIST(historic)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_MARKS(params?: any, isFilterApplied?: boolean) {
    try {
      const res: any = await markService.get(params)
      const marks = res.data.data

      if (!marks?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (marks?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_MARK_LIST(marks)
      return marks
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_EVALUATION_TYPE(params?: any, isFilterApplied?: boolean) {
    try {
      const paramsWithDefaults = {
        ...params,
        sortField: params?.sortField || 'dataAlteracao',
        sortAscending: params?.sortField ? params.sortAscending : false
      }
      const evaluationTypes = await FETCH_EVALUATION_TYPE(paramsWithDefaults)

      if (!evaluationTypes?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (evaluationTypes?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_EVALUATION_TYPE_LIST(evaluationTypes)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_PROVIDER_YARD_LIST(params?: any) {
    try {
      const providerTypes: any = await FETCH_PROVIDER_TYPE()
      const yard = providerTypes.find((type: any) => type.descricao === 'Pátio')

      if (yard?.codigo) {
        const payload = {
          CodigoTipoPrestador: yard.codigo,
          ...params
        }
        const responseYardProviders: any = await GET_PROVIDER(payload)
        const yardProviders = responseYardProviders?.rows
        SET_PROVIDER_YARD_LIST(yardProviders)
        return yardProviders
      }

      return []
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function FETCH_EVALUATION_TYPE(params?: any) {
    try {
      const res: any = await evaluationTypeService.get(params)
      return res.data.data
    } catch (e: any) {
      console.error(e.message)
    }
  }

  async function FETCH_PROVIDER_TYPE(params?: any) {
    try {
      const response: any = await providerService.getTypes(params)
      return response?.data?.data?.rows
    } catch (e: any) {
      console.error(e.message)
    }
  }

  async function GET_INSPECTION_PHOTOS(
    params?: any,
    isFilterApplied?: boolean
  ) {
    try {
      const paramsWithDefaults = {
        ...params,
        sortField: params?.sortField || 'dataAlteracao',
        sortAscending: params?.sortField ? params.sortAscending : false
      }
      const inspectionPhotos = await FETCH_INSPECTION_PHOTOS(paramsWithDefaults)

      if (!inspectionPhotos?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (inspectionPhotos?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_INSPECTION_PHOTOS_LIST(inspectionPhotos)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function FETCH_INSPECTION_PHOTOS(params?: any) {
    try {
      const res: any = await inspectionPhotosService.get(params)
      return res.data.data
    } catch (e: any) {
      console.error(e.message)
    }
  }

  async function GET_PHOTO_SECTIONS(params?: any, isFilterApplied?: boolean) {
    try {
      const paramsWithDefaults = {
        ...params,
        sortField: params?.sortField || 'dataAlteracao',
        sortAscending: params?.sortField ? params.sortAscending : false
      }
      const photoSections = await FETCH_PHOTO_SECTIONS(paramsWithDefaults)

      if (!photoSections?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (photoSections?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_PHOTO_SECTIONS_LIST(photoSections)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function FETCH_PHOTO_SECTIONS(params?: any) {
    try {
      const res: any = await photoSectionsService.get(params)
      return res.data.data
    } catch (e: any) {
      console.error(e.message)
    }
  }

  async function GET_PHOTO_FLOWS(params?: any, isFilterApplied?: boolean) {
    try {
      const paramsWithDefaults = {
        ...params,
        sortField: params?.sortField || 'dataAlteracao',
        sortAscending: params?.sortField ? params.sortAscending : false
      }
      const photoFlows = await FETCH_PHOTO_FLOW(paramsWithDefaults)

      if (!photoFlows?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (photoFlows?.rows?.length && isFilterApplied)
        toast.success(CONSTANTS.filter.success)

      SET_PHOTO_FLOW_LIST(photoFlows)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_PHOTO_FLOW(id: string) {
    try {
      const photoFlow: any = await photoFlowService.getById(id)
      return photoFlow?.data?.data
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function FETCH_PHOTO_FLOW(params?: any) {
    try {
      const res: any = await photoFlowService.get(params)
      return res.data.data
    } catch (e: any) {
      console.error(e.message)
    }
  }

  async function FETCH_CATEGORY_GROUP(params?: any) {
    try {
      const res: any = await utilityService.getCategoryGroup(params)
      return res.data.data
    } catch (e: any) {
      console.error(e.message)
    }
  }

  async function FETCH_PHOTO_FLOW_ITENS(id: string) {
    try {
      const response: any = await photoFlowService.getItens(id)
      const photoFlowItems = response?.data?.data?.rows
      if (Array.isArray(photoFlowItems) && photoFlowItems.length > 0) {
        return photoFlowItems[0] as PhotoFlowItemsByIdResponse
      }
      return null
    } catch (e: any) {
      console.error(e.message)
      return null
    }
  }

  async function ADD_PHOTO_FLOW(payload: any) {
    try {
      const response: any = await photoFlowService.post(payload)
      if (response?.status === 200 || response?.status === 201) {
        toast.success('Fluxo de Fotos cadastrado com sucesso.')
      }
      return response?.data
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function UPDATE_PHOTO_FLOW(id: string, payload: any) {
    try {
      const response: any = await photoFlowService.put(id, payload)
      if (response?.status === 200 || response?.status === 201) {
        toast.success('Fluxo de Fotos atualizado com sucesso.')
      }
      return response?.data
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function UPDATE_PHOTOS_IN_SECTION(id: string, payload: any) {
    try {
      const response: any = await photoFlowService.putPhotos(id, payload)
      if (response?.status === 200 || response?.status === 201) {
        toast.success('Fotos atualizadas com sucesso.')
      }
      return response?.data
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function ACTIVE_PHOTO_FLOW(id: string) {
    try {
      const response: any = await photoFlowService.active(id)
      if (response?.status === 200 || response?.status === 201) {
        toast.success('Fluxo de Fotos ativado com sucesso.')
      }
      return response
    } catch (e: any) {
      console.error(e.message)
    }
  }

  async function INACTIVE_PHOTO_FLOW(id: string) {
    try {
      const response: any = await photoFlowService.inactive(id)
      if (response?.status === 200 || response?.status === 201) {
        toast.success('Fluxo de Fotos inativado com sucesso.')
      }
      return response
    } catch (e: any) {
      console.error(e.message)
    }
  }

  async function VERIFY_PARAMETRIZATION_EXISTS(params: any) {
    try {
      const response: any =
        await photoFlowServiceV2.checkParametrizationExists(params)
      if (SUCCESS_STATUS.includes(response?.status)) {
        return response?.data?.data
      }
      throw new Error('Erro ao verificar a parametrização.')
    } catch (e: any) {
      console.error(e.message)
    }
  }

  async function VERIFY_THIRD_PARTY_PARAMETRIZATION_EXISTS() {
    try {
      const response: any =
        await photoFlowServiceV2.checkThirdPartyParametrizationExists()
      if (SUCCESS_STATUS.includes(response?.status)) {
        return response?.data?.data
      }
      throw new Error('Erro ao verificar a parametrização de terceiros.')
    } catch (e: any) {
      console.error(e.message)
    }
  }

  async function GET_SELECTS() {
    if (MEASUREMENT_LIST.length && BRAND_LIST.length && STATE_LIST.length)
      return null

    try {
      const res: any = await Promise.all([
        utilityService.getBrand({
          ativo: true,
          pageSize: CONSTANTS.allRegisters
        }),
        utilityService.getMeasurement(),
        addressService.getStates()
      ])
      SET_BRAND_LIST(res[0].data.data?.rows)
      SET_MEASUREMENT_LIST(res[1].data.data)
      SET_STATE_LIST(res[2].data.data)
      return res
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function SET_DATA_SELECTS() {
    let arrOfMeasurement: any = []
    let arrOfBrands: any = []
    let arrOfCategories: any = []
    let arrOfInterprise: any = []
    let arrOfStates: any = []

    MEASUREMENT_LIST.forEach((item: any) => {
      arrOfMeasurement.push(item?.nome)
    })
    BRAND_LIST.forEach((item: any) => {
      arrOfBrands.push(item?.nome)
    })
    CATEGORY_LIST?.rows?.forEach((item: any) => {
      arrOfCategories.push(item?.nome)
    })
    INTERPRISE_LIST?.rows?.forEach((item: any) => {
      arrOfInterprise.push(item?.nomeFantasia)
    })
    STATE_LIST.forEach((item: any) => {
      arrOfStates.push(item?.nome)
    })

    await SET_INTERPRISE_OPTIONS(arrOfInterprise)
    await SET_MEASUREMENT_OPTIONS(arrOfMeasurement)
    await SET_BRAND_OPTIONS(arrOfBrands)
    await SET_CATEGORY_OPTIONS(arrOfCategories)
    await SET_STATE_OPTIONS(arrOfStates)
  }

  return (
    <ParametrizationContext.Provider
      value={{
        CATEGORY_LIST,
        PROVIDER_LIST,
        PROVIDER_YARD_LIST,
        PRODUCT_LIST,
        SERVICE_LIST,
        SERIAL_ITEM_LIST,
        ACCESSORIES_LIST,
        MARK_LIST,
        SLA_LIST,
        BRAND_LIST,
        BRAND_OPTIONS,
        MEASUREMENT_LIST,
        MEASUREMENT_OPTIONS,
        BREAKDOWN_LIST,
        CATEGORY_OPTIONS,
        INTERPRISE_LIST,
        INTERPRISE_OPTIONS,
        EMPLOYEE_LIST,
        HISTORIC_LIST,
        STATE_LIST,
        STATE_OPTIONS,
        EVALUATION_TYPE_LIST,
        INSPECTION_PHOTOS_LIST,
        PHOTO_SECTIONS_LIST,
        PHOTO_FLOW_LIST,
        GET_CATEGORY,
        GET_PROVIDER,
        GET_PROVIDER_YARD_LIST,
        GET_PRODUCTS,
        GET_SERVICES,
        GET_SELECTS,
        GET_SERIAL_ITEMS,
        GET_ACCESSORIES,
        GET_MARKS,
        GET_SLA,
        GET_BREAKDOWNS,
        GET_INTERPRISE,
        GET_EMPLOYEES,
        GET_HISTORIC,
        GET_EVALUATION_TYPE,
        GET_INSPECTION_PHOTOS,
        GET_PHOTO_SECTIONS,
        GET_PHOTO_FLOWS,
        GET_PHOTO_FLOW,
        SET_EMPLOYEE_LIST,
        SET_HISTORIC_LIST,
        SET_SLA_LIST,
        SET_BREAKDOWN_LIST,
        categoriaVistoria,
        obterCategoriaVistoria,
        setCategoriaVistoria,
        FETCH_EVALUATION_TYPE,
        FETCH_CATEGORY_GROUP,
        FETCH_PHOTO_FLOW_ITENS,
        FETCH_PHOTO_SECTIONS,
        FETCH_INSPECTION_PHOTOS,
        ADD_PHOTO_FLOW,
        UPDATE_PHOTO_FLOW,
        UPDATE_PHOTOS_IN_SECTION,
        ACTIVE_PHOTO_FLOW,
        INACTIVE_PHOTO_FLOW,
        VERIFY_PARAMETRIZATION_EXISTS,
        VERIFY_THIRD_PARTY_PARAMETRIZATION_EXISTS,
        GET_DETAILS_PHOTO_FLOW,
        CLONE_PHOTO_FLOW
      }}
    >
      {children}
    </ParametrizationContext.Provider>
  )
}
